import { cx } from '../lib/cx'

/**
 * Set de iconos del panel, en SVG de trazo. Está acá y no viene de una librería
 * por dos razones del brief: cero dependencias de UI de terceros, y **prohibido
 * usar emojis como iconografía** (un emoji cambia de forma en cada sistema
 * operativo y los lectores de pantalla lo leen como palabra).
 *
 * Un icono NUNCA comunica solo: o va acompañado de texto visible, o recibe
 * `title` y entonces se anuncia con `role="img"`.
 */
export type IconName =
  | 'ok'
  | 'atencion'
  | 'critico'
  | 'sin-dato'
  | 'externo'
  | 'sin-vincular'
  | 'link'
  | 'chevron'
  | 'cerrar'
  | 'mas'
  | 'sube'
  | 'baja'
  | 'plano'
  | 'buscar'
  | 'reloj'
  | 'persona'
  | 'orden'
  | 'orden-asc'
  | 'orden-desc'
  | 'panel'
  | 'plata'
  | 'clientes'
  | 'megafono'
  | 'conversacion'
  | 'engranaje'
  | 'sincronizar'
  | 'lapiz'

const PATHS: Record<IconName, string> = {
  ok: 'M20 6 9 17l-5-5',
  atencion:
    'M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0ZM12 9v4M12 17h.01',
  critico:
    'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20ZM12 7v6M12 16.5h.01',
  'sin-dato':
    'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20ZM9.1 9a3 3 0 0 1 5.8 1c0 2-3 2.5-3 4M12 17.5h.01',
  externo: 'M7 17 17 7M9 7h8v8',
  'sin-vincular':
    'M9 17H7a5 5 0 0 1 0-10h2M15 7h2a5 5 0 0 1 4.5 7M3 3l18 18',
  link: 'M9 17H7a5 5 0 0 1 0-10h2M15 7h2a5 5 0 0 1 0 10h-2M8 12h8',
  chevron: 'm9 6 6 6-6 6',
  cerrar: 'M6 6l12 12M18 6 6 18',
  mas: 'M12 5v14M5 12h14',
  sube: 'm4 17 6-6 4 4 6-7M20 8h-4M20 8v4',
  baja: 'm4 7 6 6 4-4 6 7M20 16h-4M20 16v-4',
  plano: 'M5 12h14',
  buscar: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14ZM20 20l-4.2-4.2',
  reloj: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20ZM12 7v5l3 2',
  persona: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z',
  orden: 'm7 15 5 5 5-5M7 9l5-5 5 5',
  'orden-asc': 'm6 15 6-6 6 6',
  'orden-desc': 'm6 9 6 6 6-6',
  panel: 'M3 3h8v8H3zM13 3h8v5h-8zM13 12h8v9h-8zM3 15h8v6H3z',
  plata: 'M12 2v20M17 6.5c0-1.9-2.2-3-5-3s-5 1.1-5 3 2.2 3 5 3 5 1.1 5 3-2.2 3-5 3-5-1.1-5-3',
  clientes:
    'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8ZM22 21v-2a4 4 0 0 0-3-3.87M16 3.13A4 4 0 0 1 16 11',
  megafono: 'M3 11v2a1 1 0 0 0 1 1h2l5 4V6L6 10H4a1 1 0 0 0-1 1ZM16 9a4 4 0 0 1 0 6M19 6a8 8 0 0 1 0 12',
  conversacion: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z',
  engranaje:
    'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-2.87 1.2V21a2 2 0 1 1-4 0v-.09A1.7 1.7 0 0 0 8 19.4a1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 3 15H3a2 2 0 1 1 0-4h.09A1.7 1.7 0 0 0 4.6 8.6l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.7 1.7 0 0 0 9 6.1V6a2 2 0 1 1 4 0v.09a1.7 1.7 0 0 0 2.87 1.2l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.7 1.7 0 0 0 21 12v0a2 2 0 1 1 0 4h-.09a1.7 1.7 0 0 0-1.51 1',
  sincronizar: 'M21 12a9 9 0 0 1-15.5 6.2L3 16M3 12a9 9 0 0 1 15.5-6.2L21 8M3 21v-5h5M21 3v5h-5',
  lapiz: 'M17 3a2.8 2.8 0 0 1 4 4L7.5 20.5 2 22l1.5-5.5Z',
}

export interface IconProps {
  name: IconName
  /** Lado del cuadrado en px. Por defecto 14: la medida de la interfaz densa. */
  size?: number
  /** Nombre accesible. Sin él el icono es decorativo (`aria-hidden`). */
  title?: string
  className?: string
  /** Grosor del trazo. Los iconos chicos necesitan 2 para no desaparecer. */
  strokeWidth?: number
}

export function Icon({
  name,
  size = 14,
  title,
  className,
  strokeWidth = 2,
}: IconProps) {
  const decorativo = title === undefined

  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      focusable="false"
      aria-hidden={decorativo ? true : undefined}
      role={decorativo ? undefined : 'img'}
      aria-label={title}
      data-icon={name}
      className={cx('block shrink-0', className)}
    >
      <path d={PATHS[name]} />
    </svg>
  )
}

/** Icono que le corresponde a cada severidad. Acompaña siempre al color. */
export const ICONO_SEVERIDAD = {
  ok: 'ok',
  warning: 'atencion',
  critical: 'critico',
  unknown: 'sin-dato',
} as const satisfies Record<string, IconName>
